<script setup>

import HeaderTitle from "@/components/layout/HeaderTitle.vue";
import TicketStatusService from "@/api/services/ticket.status.service";
import TicketTypeService from "@/api/services/ticket.type.service";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";
import { h, onMounted, ref, shallowRef, computed, onUnmounted, watch, nextTick } from "vue";
import InternalTicketsRequested from "@/views/internal-tickets/InternalTicketsRequested.vue";
import InternalTicketsDepartment from "@/views/internal-tickets/InternalTicketsDepartment.vue";
import InternalTicketsAssigned from "@/views/internal-tickets/InternalTicketsAssigned.vue";
import TicketPriorityService from "@/api/services/ticket.priority.service";
import TicketService from "@/api/services/ticket.service";
import Notification from "@/utils/plugins/NotificationService";
import { departmentsWithTickets, loadDepartmentTicketTotals } from "@/views/internal-tickets/departmentTicketTotals";
import { useRouter } from "vue-router";

const authStore = useAuthStore();
const uiStore = useUiStore();
const router = useRouter();

const customPrefix = shallowRef({
  render() {
    return h('img', {
      src: '/calendar-blue.svg',
      style: 'width: 16px; height: 16px; margin-right: 10px;',
    });
  },
})

const ticketTotals = ref({
  assigned: 0,
  requested: 0,
});
const totalTicketsResults = computed(() => {
  return Object.values(ticketTotals.value).reduce((total, sectionTotal) => total + sectionTotal, 0);
});
const refreshData = ref(false);
const tag = ref("");
const issue = ref("");
const type = ref(null);
const types = ref([]);
const priority = ref(null);
const priorities = ref([]);
const selectedFromDate = ref(null);
const selectedToDate = ref(null);
const statusTicket = ref(null);
const optionsStatus = ref([]);
const refreshInterval = 5 * 60 * 1000; // 5 minutes
let intervalId = null;
const activeTab = ref('assigned');
const initialLoadComplete = ref(false);
const tabsReady = ref(false);
watch(() => [initialLoadComplete.value, uiStore.loading], ([done, loading]) => {
  if (done && !loading) tabsReady.value = true;
}, { immediate: true });
let disposed = false;
// Mantener una única carga inicial mientras se descubren y montan los listados.
uiStore.startLoading();

const notCollaborator = computed(() => {
  if (authStore.getUserLogged) {
    return !authStore.getUserLogged.roles.includes("ROLE_EXTERNAL_COLLABORATOR");
  }
  return true;
})

const managedDepartments = computed(() => Array.isArray(authStore.getUserLogged?.managedDepartments)
  ? authStore.getUserLogged.managedDepartments.filter(department => department?.id != null)
  : []);
const departmentTotals = ref({});
const departmentTotalsError = ref(false);
const visibleDepartments = computed(() => departmentsWithTickets(managedDepartments.value, departmentTotals.value));
let latestDepartmentTotalsRequest = 0;

const refreshDepartmentTotals = async () => {
  const requestId = ++latestDepartmentTotalsRequest;
  const departments = managedDepartments.value;
  if (!departments.length) {
    departmentTotals.value = {};
    departmentTotalsError.value = false;
    ticketTotals.value = Object.fromEntries(Object.entries(ticketTotals.value).filter(
      ([section]) => !section.startsWith('department-')
    ));
    if (activeTab.value.startsWith('department-')) activeTab.value = 'assigned';
    return;
  }
  uiStore.startLoading();
  try {
    const results = await loadDepartmentTicketTotals(departments, departmentTotals.value, TicketService.getTickets);
    if (requestId !== latestDepartmentTotalsRequest) return;
    departmentTotalsError.value = results.some(([, , failed]) => failed);
    departmentTotals.value = Object.fromEntries(results.map(([id, total]) => [id, total]));
    const visibleIds = new Set(results.filter(([, total]) => total > 0).map(([id]) => String(id)));
    ticketTotals.value = Object.fromEntries(Object.entries(ticketTotals.value).filter(([section]) =>
      !section.startsWith('department-') || visibleIds.has(section.slice('department-'.length))
    ));
    if (activeTab.value.startsWith('department-') &&
      !visibleDepartments.value.some(department => activeTab.value === 'department-' + department.id)) {
      activeTab.value = 'assigned';
    }
  } finally {
    uiStore.stopLoading();
  }
};

watch(() => managedDepartments.value.map(department => String(department.id)).sort().join(','), () => {
  refreshDepartmentTotals();
});

const setupAutoRefresh = () => {
  intervalId = setInterval(() => {
    resetTicketTotals();
    refreshData.value = !refreshData.value;
    refreshDepartmentTotals();
  }, refreshInterval);
}

const clearAutoRefresh = () => {
  clearInterval(intervalId);
}

onMounted(async () => {
  try {
    const results = await Promise.allSettled([getTypes(), getStates(), getPriorities(), refreshDepartmentTotals()]);
    if (results.some(result => result.status === 'rejected')) {
      Notification.addNotification('No se han podido cargar todos los datos de tickets.', 'error', 6000);
    }
    // Los listados de departamento se montan al conocer los totales.
    await nextTick();
  } finally {
    if (!disposed) {
      initialLoadComplete.value = true;
      setupAutoRefresh();
    }
    uiStore.stopLoading();
  }
})

onUnmounted(() => {
  disposed = true;
  latestDepartmentTotalsRequest++;
  clearAutoRefresh();
})

const search = async () => {
  resetTicketTotals();
  refreshData.value = !refreshData.value;
  await refreshDepartmentTotals();
}

const getStates = async () => {
  const response = await TicketStatusService.getStates({ itemsPerPage: 999999, page: 1 });
  optionsStatus.value = response.data['hydra:member'];
}

const getTypes = async () => {
  const response = await TicketTypeService.getTypes({ itemsPerPage: 999999, page: 1 });
  types.value = response.data['hydra:member'];
}

const getPriorities = async () => {
  const response = await TicketPriorityService.getPriorities({ itemsPerPage: 999999, page: 1 });
  priorities.value = response.data['hydra:member'];
}

const resetTicketTotals = () => {
  ticketTotals.value = {
    assigned: 0,
    requested: 0,
  };
}

const setTotalTicket = (section, total) => {
  ticketTotals.value = {
    ...ticketTotals.value,
    [section]: Number(total) || 0,
  };
}

// Combina los filtros fijos con los opcionales seleccionados
const combinedFilters = computed(() => {
  const filters = {}
  if (statusTicket.value) {
    // pasar statusTicket.value en mayusculas
    if (statusTicket.value === 'Enviado' || statusTicket.value === 'Abierto' || statusTicket.value === 'Reabierto') {
      filters['status'] = 'ABIERTO';
    }
    if (statusTicket.value === 'Trabajando' || statusTicket.value === 'En espera') {
      filters['status'] = 'EN CURSO';
    }
    if (statusTicket.value === 'Cerrado') {
      filters['status'] = statusTicket.value.toUpperCase();
    }
    // filters['status'] = statusTicket.value.toUpperCase();
  }
  if (type.value) {
    filters['ticketType.id'] = type.value;
  }

  if (priority.value) {
    filters['ticketPriority.id'] = priority.value;
  }

  if (tag.value) {
    // filters.tag = tag.value;
    filters.ticketCode = tag.value;
  }

  if (issue.value) {
    filters.issue = issue.value;
  }

  if (selectedFromDate.value && selectedToDate) {
    filters["createdAt[before]"] = selectedToDate.value;
    filters["createdAt[after]"] = selectedFromDate.value;
  }
  return filters;
});

const setActiveTab = (tabId) => {
  activeTab.value = tabId;
}

const newInternalTicket = () => {
  router.push({ name: 'new_internal_ticket' });
}

</script>

<template>
  <div id="tickets">
    <header-title title="Tickets Internos" />
    <el-row>
      <el-card class="card-search w-100 wback" body-style="padding:0">
        <div class="row mx-5">
          <div class="col-xl-3 col-md-6 col-lg-4 el-col-xs-24">
            <div class="form-item date-picker mt-0 w-100 label-common pl-5">
              <label for="ffrom" class="w-100">Fecha desde</label>
              <el-date-picker id="ffrom" v-model="selectedFromDate" name="from-dat'" type="date"
                class="select-internal-tickets" placeholder="Fecha desde" format="YYYY/MM/DD" value-format="YYYY-MM-DD"
                :prefix-icon="customPrefix" :picker-options="{ firstDayOfWeek: 1 }" @change="search()" />
            </div>
          </div>
          <div class="col-xl-3 col-md-6 col-lg-4 el-col-xs-24">
            <div class="form-item date-picker mt-0 w-100 label-common pl-5">
              <label for="fto" class="w-100">Fecha hasta</label>
              <el-date-picker id="fto" v-model="selectedToDate" name="from-date" type="date" placeholder="Fecha hasta"
                class="select-internal-tickets" format="YYYY/MM/DD" value-format="YYYY-MM-DD"
                :prefix-icon="customPrefix" :picker-options="{ firstDayOfWeek: 1 }" @change="search()" />
            </div>
          </div>
          <div class="col-xl-3 col-md-6 col-lg-4 el-col-xs-24">
            <div class="form-item mt-0 w-100 label-common pl-5">
              <label for="id-status" class="w-100">Estado</label>
              <el-select v-model="statusTicket" filterable clearable @change="search()">
                <el-option v-for="item in optionsStatus" :key="item.id" :label="item.name" :value="item.name" />
              </el-select>
            </div>
          </div>
          <div class="col-xl-3 col-md-4 col-lg-4 el-col-xs-24">
            <div class="form-item mt-0 w-100 label-common">
              <label for="domain-name" class="w-100">Código</label>
              <el-input id="title" v-model="tag" class="search-input" placeholder="Código" clearable
                @change="search()" />
            </div>
          </div>
          <div class="col-xl-3 col-md-3 col-lg-3 el-col-xs-24">
            <div class="form-item label-common">
              <label class="w-100 mt-0">Tipo</label>
              <el-select v-model="type" filterable clearable placeholder="TODOS" @change="search()"
                class="select-internal-tickets">
                <el-option v-for="item in types" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </div>
          </div>
          <div class="col-xl-3 col-md-3 col-lg-3 el-col-xs-24">
            <div class="form-item label-common">
              <label class="w-100 mt-0">Prioridad</label>
              <el-select v-model="priority" filterable clearable placeholder="TODOS" @change="search()"
                class="select-internal-tickets">
                <el-option v-for="item in priorities" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </div>
          </div>
          <div class="col-xl-6 col-md-6 col-lg-6 el-col-xs-24">
            <div class="form-item mt-0 w-100 label-common">
              <label for="domain-name" class="w-100">Asunto</label>
              <el-input id="title" v-model="issue" class="search-input" placeholder="Asunto" clearable
                @change="search()" />
            </div>
          </div>
        </div>
        <div
          class="col-xl-12 col-md-12 col-lg-12 el-col-xs-24 mt-0 pe-5 d-flex justify-content-end align-items-end actions-container"
          style="padding-right: 0;">
          <button class="btn btn_darkpink btn-new mb-3" @click="newInternalTicket()" v-if="notCollaborator">
            NUEVO TICKET
          </button>
          <button class="btn btn_yellow btn-search mb-3" @click="search()">
            Buscar
          </button>
        </div>
      </el-card>
      <div class="mt-2 tickets-results" v-if="tabsReady">
        <span class="text-result">{{ totalTicketsResults }}</span>&nbsp;
        <span class="text-result-blue">resultados sumados de las pestañas</span>
      </div>
    </el-row>
    <div v-if="tabsReady && departmentTotalsError" class="alert alert-warning mt-3" role="alert">
      No se han podido comprobar todos los departamentos; alguna pestaña podría faltar.
      <button type="button" class="btn btn-link" @click="refreshDepartmentTotals">Reintentar</button>
    </div>
    <nav class="mt-3" v-if="tabsReady">
      <div class="nav nav-tabs" id="nav-tab" role="tablist">
        <button :class="['nav-link', { active: activeTab === 'assigned' }]" id="assigned-tab"
          @click="setActiveTab('assigned')" type="button" role="tab"
          aria-controls="assigned" :aria-selected="activeTab === 'assigned'">
          <div class="d-flex">
            <img src="@/assets/icons/group.svg" width="30" alt="tickets asignados">
            <div class="title-text pl-4">
              Tickets asignados
            </div>
          </div>
        </button>
        <button v-for="department in visibleDepartments" :key="department.id"
          :class="['nav-link', { active: activeTab === 'department-' + department.id }]"
          :id="'department-tab-' + department.id" @click="setActiveTab('department-' + department.id)"
          type="button" role="tab" :aria-controls="'department-' + department.id"
          :aria-selected="activeTab === 'department-' + department.id">
          <div class="d-flex">
            <img src="@/assets/icons/group.svg" width="30" alt="">
            <div class="title-text pl-4">
              Tickets {{ department.name }} ({{ departmentTotals[department.id] }})
            </div>
          </div>
        </button>
        <button :class="['nav-link', { active: activeTab === 'requested' }]" id="requested-tab"
          @click="setActiveTab('requested')" type="button" role="tab"
          aria-controls="requested" :aria-selected="activeTab === 'requested'" v-if="notCollaborator">
          <div class="d-flex">
            <img src="@/assets/icons/group.svg" width="30" alt="Tickets solicitados">
            <div class="title-text pl-4">
              Tickets solicitados
            </div>
          </div>
        </button>
      </div>
    </nav>
    <div class="tab-content" id="nav-tabContent">
      <div v-show="activeTab === 'assigned'" class="tab-pane fade show active" id="assigned" role="tabpanel"
        aria-labelledby="assigned-tab">
        <InternalTicketsAssigned @totalTicket="setTotalTicket('assigned', $event)" :refreshData="refreshData"
          :filters="combinedFilters" />
      </div>

      <div v-for="department in visibleDepartments" :key="department.id"
        v-show="activeTab === 'department-' + department.id" class="tab-pane fade show active"
        :id="'department-' + department.id" role="tabpanel" :aria-labelledby="'department-tab-' + department.id">
        <InternalTicketsDepartment :department-id="department.id"
          @totalTicket="setTotalTicket('department-' + department.id, $event)" :refreshData="refreshData"
          :filters="combinedFilters" />
      </div>

      <div v-show="activeTab === 'requested'" class="tab-pane fade show active" id="requested" role="tabpanel"
        aria-labelledby="requested-tab">
        <el-row>
          <InternalTicketsRequested @totalTicket="setTotalTicket('requested', $event)" :refreshData="refreshData"
            :filters="combinedFilters" />
        </el-row>
        <!--        <el-row class="text-right">-->
        <!--          <el-col>-->
        <!--            <router-link-->
        <!--                :to="{name: 'new_internal_ticket'}"-->
        <!--                class="btn btn_darkpink search btn-new"-->
        <!--            >-->
        <!--              NUEVO TICKET-->
        <!--            </router-link>-->
        <!--          </el-col>-->
        <!--        </el-row>-->
      </div>
    </div>


  </div>
</template>

<style scoped lang="scss">
.select-internal-tickets {
  width: 90% !important;
}

#tickets {
  .buttons {
    margin-left: auto;
  }

  .type-color {
    width: 15px;
    height: 15px;
    border-radius: 50%;
    margin-right: 5px;
  }

  .title-text {
    text-align: center;
    align-self: center;

    font-size: 3em;
    font-weight: 900;
    letter-spacing: -0.05em;
    color: $font-blue-color;
  }

  .card-search {
    border-radius: 15px;
    height: 250px;
    background: url("../../assets/icons/altavoz.png"), white;
    background-repeat: no-repeat;
    background-position-x: 100%;
    overflow: visible !important;
  }

  #icon-search {
    fill: $bg-blue;
    width: 6%;
  }

  .label-common {
    label {
      font-size: 20px;
      font-weight: 800;
      letter-spacing: -2px;
    }
  }

  .btn-new {
    width: 20em;
    height: 3em;
    color: white;
    font-weight: bolder;
    text-transform: uppercase;
  }

  .table-domains {
    table {
      thead {
        tr {
          th {
            padding: 0 !important;
            margin: 0 !important;
          }
        }

        #icon-search {
          fill: $bg-blue;
          width: 2.9em;
        }

        #icon-user {
          fill: $bg-blue;
          width: 1.6em;
        }
      }

      tbody {
        td {
          .link-name {
            margin: auto;
            margin-left: 10px;
          }
        }
      }
    }
  }

  .close-icon {
    width: 30px;
    height: 30px;
  }

  #asterisk {
    position: absolute;
    vertical-align: super;
    height: auto;
    width: 2.2em;
    fill: #f9156f;
  }

  .el-dialog__header {
    padding: 0;
  }

  .my-header {
    margin-top: 2px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .text-header {
    text-align: left;
    font-size: 4em;
    font-weight: 900;
    letter-spacing: -0.05em;
    color: #f9156f;
  }

  .text-result {
    text-align: left;
    font-size: 2em;
    font-weight: 900;
    letter-spacing: -0.05em;
    color: #f9156f;

  }

  .text-result-blue {
    text-align: left;
    font-size: 2em;
    font-weight: 400;
    letter-spacing: -0.05em;
    color: $bg-blue;

  }

  .wback {
    background-image: url('../../assets/images/ticket-back.png');
    background-repeat: no-repeat;
    background-size: contain;
  }
}

.actions-container {
  display: flex;
  justify-content: flex-end;

  @media (max-width: 767px) {
    justify-content: center !important;
    flex-direction: column;
    align-items: center !important;
    padding-right: 0px !important;
  }

  button {
    @media (max-width: 767px) {
      width: 20em;
      max-width: 400px;
    }
  }
}

@media(max-width:767px) {
  .tickets-results {
    white-space: nowrap;
  }

  #tickets .tickets-results .text-result,
  #tickets .tickets-results .text-result-blue {
    font-size: min(4.8vw, 26px);
  }

  #nav-tab {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  #nav-tab .nav-link {
    width: 100%;
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
    text-align: left;
  }

  #nav-tab .title-text {
    min-width: 0;
    font-size: 2em;
  }

  #tickets .card-search {
    height: auto;
  }

  #tickets .card-search .row {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  #tickets .card-search .form-item {
    padding-left: 0 !important;
  }

  .select-internal-tickets {
    width: 100% !important;
  }
}
</style>
